import { useState } from 'react';
import { useVantifyContext } from 'integrations-vantify-context-providers';
import {
  getAllSites,
  getSiteAssociates,
  SiteListResponse,
  VantifyQuery,
} from 'integrations-vantify-client-services';
import { useVantifyBase } from '../useVantifyBase';
import { VantifyOperation, hookMessages } from '../common';
import type { SiteAssociateUser } from './types';

export function useVantifySites() {
  const { auth } = useVantifyContext();
  const { loading, error, execute, setError } = useVantifyBase();
  const [sites, setSites] = useState<SiteListResponse | null>(null);

  const [siteAssociates, setSiteAssociates] = 
  useState<SiteAssociateUser[] | null>(null);

  const fetchSites = async (fetchQuery?: VantifyQuery) => {
    const queryToUse = fetchQuery ?? {};

    if (!auth) {
      setError(e => ({
        ...e,
        [VantifyOperation.FETCH_SITES]: hookMessages.error_FETCH_SITES,
      }));

      return null;
    }

    const response = await execute(
      VantifyOperation.FETCH_SITES,
      () => getAllSites(auth, queryToUse),
      hookMessages.error_FETCH_SITES
    );

    if (response?.data) {
      setSites(response.data);

      return response.data;
    }

    return null;
  };

  const fetchSiteAssociates = async (
    siteId: number,
    query?: VantifyQuery
  ) => {
    if (!auth) {
      setError(e => ({
        ...e,
        [VantifyOperation.FETCH_SITE_ASSOCIATES]:
          hookMessages.error_FETCH_SITE_ASSOCIATES,
      }));

      return null;
    }

    const response = await execute(
      VantifyOperation.FETCH_SITE_ASSOCIATES,
      () => getSiteAssociates(auth, siteId, query),
      hookMessages.error_FETCH_SITE_ASSOCIATES
    );

    if (response?.data) {
      const mappedAssociates: SiteAssociateUser[] = response.data.associates
        .map(associate => {
          const userLink = associate._links?.user;

          if (!userLink || !userLink.id) {
            return null;
          }

          return {
            id: userLink.id,
            title: userLink.title,
            email: userLink.email,
            href: userLink.href,
          };
        })
        .filter(
          (user): user is SiteAssociateUser => user !== null
        );

      setSiteAssociates(mappedAssociates);

      return mappedAssociates;
    }

    return null;
  };

  return {
    sites,
    siteAssociates,
    loading,
    error,
    fetchSites,
    fetchSiteAssociates,
  };
}
